import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import UiKit from '../../../../../shared/ui/ui-kit';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { SubmittingStatus } from '../../../../../shared/infrastructure/types/common/common-types';
import { useTranslation } from 'react-i18next';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';

interface RegisterFormProps {}

let RegisterForm = function ({}: RegisterFormProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const registerUIManager = authorizationUiModule.registerUIManager;
  const coreManager = registerUIManager.coreManager;
  const yandexCloudManager = authorizationUiModule.module.yandexCloudManager;
  const errors = coreManager.registerErrors;
  const showValidation = coreManager.formStatus === SubmittingStatus.ERROR_SUBMITTING;

  const companyError = errors?.company_name;
  const fioError =
    errors?.name ??
    (showValidation && !coreManager.validator.fio.isValid
      ? t(coreManager.validator.fio.errors[0])
      : undefined);
  const emailError =
    errors?.email ??
    (showValidation && !coreManager.validator.login.isValid
      ? t(coreManager.validator.login.errors[0])
      : undefined);
  const passwordError =
    errors?.password ??
    (showValidation && !coreManager.validator.password.isValid
      ? t(coreManager.validator.password.errors[0])
      : undefined);
  const promoError = errors?.promo;
  const generalError = coreManager.registerGeneralError;

  return (
    <Wrapper>
      {generalError && <GeneralError>{generalError}</GeneralError>}
      <FieldsGroup>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('register.label.companyName')}>
          <UiKit.form.Validation
            validateStatus={companyError ? 'error' : ''}
            message={companyError ?? ''}
            turnValidation={!!companyError}
          >
            <UiKit.Input
              value={coreManager.companyName}
              onChange={value => coreManager.setCompanyName(value as CompanyName)}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('register.label.fio')}>
          <UiKit.form.Validation
            validateStatus={fioError ? 'error' : ''}
            message={fioError as string}
            turnValidation={!!fioError}
          >
            <UiKit.Input
              value={coreManager.fio}
              onChange={value => coreManager.setFio(value as FIO)}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('register.label.email')}>
          <UiKit.form.Validation
            validateStatus={emailError ? 'error' : ''}
            message={emailError as string}
            turnValidation={!!emailError}
          >
            <UiKit.Input
              value={coreManager.login}
              onChange={value => coreManager.setLogin(value as LoginEmail)}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <OperatorRow>
          <UiKit.Checkbox
            checked={coreManager.isOperator}
            onChange={value => coreManager.setOperator(value)}
          >
            <OperatorLabel>{t('register.label.operator')}</OperatorLabel>
          </UiKit.Checkbox>
          <UiKit.Tooltip text={t('register.operator.tooltip')}>
            <OperatorHint>
              <UiKit.icons.QuestionCircle />
            </OperatorHint>
          </UiKit.Tooltip>
        </OperatorRow>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('register.label.password')}>
          <UiKit.form.Validation
            validateStatus={passwordError ? 'error' : ''}
            message={passwordError as string}
            turnValidation={!!passwordError}
          >
            <UiKit.Input
              value={coreManager.password}
              onChange={value => coreManager.setPassword(value as LoginPassword)}
              type="password"
              showPasswordVisibilityToggle={true}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
        <UiKit.form.Item.VerticalLayoutTemplate label={t('register.label.promocode')}>
          <UiKit.form.Validation
            validateStatus={promoError ? 'error' : ''}
            message={promoError ?? ''}
            turnValidation={!!promoError}
          >
            <UiKit.Input
              value={coreManager.promoCode}
              onChange={value => coreManager.setPromoCode(value as PromoCode)}
            />
          </UiKit.form.Validation>
        </UiKit.form.Item.VerticalLayoutTemplate>
      </FieldsGroup>
      <UiKit.Button
        loading={coreManager.formStatus === SubmittingStatus.SUBMITTING}
        disabled={!coreManager.login || yandexCloudManager.isBlocked}
        customStyles={{ width: '100%' }}
        type="primary"
        onClick={() => coreManager.submitRegister()}
      >
        {t('register.submit.button')}
      </UiKit.Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const GeneralError = styled.div`
  font-size: 14px;
  color: ${colorsPalette.red600};
`;
const FieldsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const OperatorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const OperatorLabel = styled.span`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.88);
`;
const OperatorHint = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
`;

RegisterForm = observer(RegisterForm);

export { RegisterForm };
